import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators,
} from '@angular/forms'
import {
  AppointmentModel,
  AuthorizationModel,
  Availity,
  AvailityServiceReview,
  ClinicProvider,
  ClinicService,
  FacilityService,
  InsuranceProviderConfigurationModel,
  InsuranceProviderLookup,
  Patient,
  PatientInsurance,
  ResourcesService,
  stripPhone,
} from '@mmx/shared'
import { uniq } from 'lodash'
import moment from 'moment'
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs'
import { combineLatestWith, filter } from 'rxjs/operators'

import { ResourceSelectorFieldComponent } from '../../../resource-selector'
import { AuthForm } from '../../utils'

interface RenderingProviderFacilityOption {
  type: AvailityServiceReview.RenderingProviderRoleCode
  name: string
  provider: ClinicProvider
  phone: string
}

@Component({
  selector: 'mmx-prior-authorization-form',
  templateUrl: './prior-authorization-form.component.html',
  styleUrls: ['./prior-authorization-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PriorAuthorizationFormComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: PriorAuthorizationFormComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class PriorAuthorizationFormComponent
implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  @Input()
  public configuration: InsuranceProviderConfigurationModel

  @Input()
  public data!: ElementFormData

  @Input()
  public carrier: InsuranceProviderLookup

  @Input()
  public readonlyMode = false

  @ViewChild('resourceInput') resourceInput: ResourceSelectorFieldComponent

  public authForm: AuthForm
  public renderingProviderControl = new FormControl<string>(null)
  public facilityControl = new FormControl(null, [Validators.required])
  public renderingProviderFacilityOptions: RenderingProviderFacilityOption[] =
    []

  private readonly onDestroy$ = new Subject<void>()
  private onChange: ((value: Record<string, any>) => void) | undefined
  private onTouched: (() => void) | undefined

  private readonly resources$ = this.resourcesService.resources$.pipe(
    takeUntil(this.onDestroy$),
  )

  constructor(
    private readonly clinicService: ClinicService,
    private readonly facilityService: FacilityService,
    private readonly resourcesService: ResourcesService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.renderingProviderFacilityOptions.push({
      name: this.clinicService.clinic.name,
      provider: this.clinicService.clinic.provider,
      type: AvailityServiceReview.RenderingProviderRoleCode.Clinic,
      phone: stripPhone(this.clinicService.clinic.phoneNumber),
    })

    for (const facility of this.facilityService.facilities) {
      if (facility.provider.id && facility.provider.taxId) {
        this.renderingProviderFacilityOptions.push({
          name: facility.name,
          provider: facility.provider,
          type: AvailityServiceReview.RenderingProviderRoleCode.Facility,
          phone: stripPhone(
            facility.phoneNumber || this.clinicService.clinic.phoneNumber,
          ),
        })
      }
    }
  }

  public ngOnInit(): void {
    this.facilityControl.valueChanges
      .pipe(takeUntil(this.onDestroy$), filter(Boolean))
      .subscribe((value: RenderingProviderFacilityOption) => {
        this.authForm.facilityForm.patchValue({
          lastName: value.provider.name,
          taxId: value.provider.taxId,
          npi: value.provider.id,
          roleCode: value.type,
          phone: value.phone,
        })
      })

    this.renderingProviderControl.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        distinctUntilChanged(),
        combineLatestWith(this.resources$),
      )
      .subscribe(([id, resources]) => {
        if (id?.length) {
          this.authForm.renderingProviderForm.enable()
          const resource = resources.find((res) => res.id === id)
          this.authForm.renderingProviderForm.patchValue({
            lastName: resource?.name,
            npi: resource?.npi,
            taxId: resource?.taxId,
            roleCode:
              AvailityServiceReview.RenderingProviderRoleCode
                .AttendingPhysician,
          })
        } else {
          this.authForm.renderingProviderForm.patchValue({
            lastName: null,
            npi: null,
            taxId: null,
            roleCode: null,
          })
          this.authForm.renderingProviderForm.disable()
        }

        this.authForm.renderingProviderForm.updateValueAndValidity()
        this.cdr.detectChanges()
      })

    this.authForm = new AuthForm(this.configuration)
    this.authForm.form.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.onChange?.(value)
      })
    this.initValues()
  }

  public ngOnDestroy(): void {
    this.authForm.destroy()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public writeValue(value: Partial<AvailityServiceReview.Review>): void {
    this.authForm.form.patchValue(value)
  }

  public registerOnChange(
    fn: (value: Partial<AvailityServiceReview.Review>) => void,
  ): void {
    this.onChange = fn
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  public validate() {
    return this.authForm.form.valid ? null : { invalid: true }
  }

  public isRequired(control: AbstractControl) {
    return control.hasValidator(Validators.required)
  }

  public getValueByCode(
    code: string,
    codeValues: AvailityServiceReview.CodeValue[],
  ): string {
    const codeValue = codeValues?.find((cv) => cv.code === code)

    return codeValue?.value
  }

  public formatDateString(dateString: string): string {
    const mom = moment(dateString)
    if (!mom.isValid()) {
      return ''
    }

    return mom.format('MM/DD/YYYY')
  }

  private initValues() {
    const cptCodes: string[] = this.data.authorization.procedureCodes ?? []
    const serviceCodes: string[] = []

    if (this.data.appointment?.products) {
      for (const product of this.data.appointment.products) {
        cptCodes.push(...product.getCPTCodes())
        if (product.serviceCodes) {
          const validCodes = product.serviceCodes.filter((code) => {
            return (
              this.authForm.codeValueMap.serviceTypeCode?.findIndex(
                (serviceType) => serviceType.code === code,
              ) !== -1
            )
          })
          serviceCodes.push(...validCodes)
        }
      }
    }

    const procedures: Partial<AvailityServiceReview.Procedure>[] = uniq(
      cptCodes,
    ).map((code) => {
      return {
        code,
        quantity: '1',
      }
    })

    const diagnoses: Partial<AvailityServiceReview.Diagnosis>[] =
      this.data.appointment.studies
        ?.map((study) => {
          return study.diagnosisCodes?.map((diagnosisCode) => ({
            code: diagnosisCode.code,
            date: study.startAt,
          }))
        })
        .flat()
        .filter(Boolean)

    const resources = this.data.appointment.resources ?? []
    const providers = resources.filter(
      (resource) => resource.type === 'provider',
    )

    if (providers.length > 0) {
      this.renderingProviderControl.setValue(providers[0].id)
    }

    this.authForm.form.patchValue({
      requestingProvider: {
        lastName: this.clinicService.clinic.provider.name,
        npi: this.clinicService.clinic.provider.id,
        taxId: this.clinicService.clinic.provider.taxId,
        contactName: this.clinicService.clinic.provider.name,
        phone: stripPhone(this.clinicService.clinic.phoneNumber),
      },
      subscriber: {
        lastName: this.data.patient?.name.family,
        firstName: this.data.patient?.name.given,
        middleName: this.data.patient?.name.middle,
        suffix: this.data.patient?.name.suffix,
        birthDate: this.data.patient?.dob.toISOString(),
        memberId: this.data.patientInsurance?.memberId,
      },
      patient: {
        lastName: this.data.patient?.name.family,
        firstName: this.data.patient?.name.given,
        middleName: this.data.patient?.name.middle,
        suffix: this.data.patient?.name.suffix,
        birthDate: this.data.patient?.dob.toISOString(),
        genderCode: this.authForm.coerceSex(this.data.patient?.sex),
      },
      serviceTypeCode: serviceCodes?.[0],
      placeOfServiceCode: this.transformPlaceOfService(
        this.data.appointment.location?.placeOfServiceTypes?.[0],
      ),
      fromDate: this.data.appointment?.startAt?.toISOString(),
      toDate: this.data.appointment?.endAt?.toISOString(),
    })

    for (let i = 0; i < serviceCodes.length - 1; i++) {
      if (this.authForm.canAddAdditionalServiceTypes) {
        this.authForm.addAdditionalServiceTypeRow()
        // serviceCodes[i - 1] since first service code is used for primary serviceTypeCode field
        this.authForm.additionalServiceTypes.controls[i].patchValue(
          serviceCodes[i - 1],
        )
      } else {
        break
      }
    }

    for (let i = 0; i < procedures.length; i++) {
      let control = this.authForm.procedures.controls[i]
      if (!control) {
        if (!this.authForm.canAddProcedure) {
          break
        }

        this.authForm.addProcedureRow()
        control = this.authForm.procedures.controls[i]
      }

      if (control) {
        control.patchValue(procedures[i])
      }
    }

    if (diagnoses) {
      for (let i = 0; i < diagnoses.length; i++) {
        let control = this.authForm.diagnoses.controls[i]
        if (!control) {
          if (!this.authForm.canAddDiagnosis) {
            break
          }

          this.authForm.addDiagnosesRow()
          control = this.authForm.diagnoses.controls[i]
        }

        if (control) {
          control.patchValue({
            code: diagnoses[i].code,
            date: diagnoses[i].date,
          })
        }
      }
    }

    if (this.data.appointment?.location) {
      for (const option of this.renderingProviderFacilityOptions) {
        if (
          option.provider.id === this.data.appointment.location.provider?.id
        ) {
          this.facilityControl.patchValue(option)
          break
        }
      }
    }

    // set clinic as a facility rendering provider if nothing is set before
    if (!this.facilityControl.value) {
      const clinicOption = this.renderingProviderFacilityOptions.find(
        (option) =>
          option.type ===
          AvailityServiceReview.RenderingProviderRoleCode.Clinic,
      )
      this.facilityControl.patchValue(clinicOption)
    }

    this.authForm.form.updateValueAndValidity()
  }

  private transformPlaceOfService(
    placeOfServiceCode?: string,
  ): string | undefined {
    if (placeOfServiceCode) {
      switch (placeOfServiceCode) {
        case 'pharmacy':
          return Availity.PlaceOfService.PHARMACY
        case 'school':
          return Availity.PlaceOfService.SCHOOL
        case 'shelter':
          return Availity.PlaceOfService.HOMELESS_SHELTER
        case 'indian_freestanding':
          return Availity.PlaceOfService
            .INDIAN_HEALTH_SERVICE_FREE_STANDING_FACILITY
        case 'indian_provider':
          return Availity.PlaceOfService
            .INDIAN_HEALTH_SERVICE_PROVIDER_BASED_FACILITY
        case 'tribal_638_freestanding':
          return Availity.PlaceOfService.TRIBAL_638_FREE_STANDING_FACILITY
        case 'tribal_638_provider':
          return Availity.PlaceOfService.TRIBAL_638_PROVIDER_BASED_FACILITY
        case 'office':
          return Availity.PlaceOfService.OFFICE
        case 'home':
          return Availity.PlaceOfService.HOME
        case 'assisted_living':
          return Availity.PlaceOfService.ASSISTED_LIVING_FACILITY
        case 'group_home':
          return Availity.PlaceOfService.GROUP_HOME
        case 'mobile_unit':
          return Availity.PlaceOfService.MOBILE_UNIT
        case 'off_campus_outpatient_hospital':
          return Availity.PlaceOfService.OFF_CAMPUS_OUTPATIENT_HOSPITAL
        case 'urgent_care':
          return Availity.PlaceOfService.URGENT_CARE_FACILITY
        case 'inpatient_hospital':
          return Availity.PlaceOfService.INPATIENT_HOSPITAL
        case 'on_campus_outpatient_hospital':
          return Availity.PlaceOfService.ON_CAMPUS_OUTPATIENT_HOSPITAL
        case 'er_hospital':
          return Availity.PlaceOfService.EMERGENCY_ROOM_HOSPITAL
        case 'surgical_center':
          return Availity.PlaceOfService.AMBULATORY_SURGICAL_CENTER
        case 'birthing_center':
          return Availity.PlaceOfService.BIRTHING_CENTER
        case 'military':
          return Availity.PlaceOfService.MILITARY_TREATMENT_FACILITY
        case 'skilled_nursing':
          return Availity.PlaceOfService.SKILLED_NURSING_FACILITY
        case 'nursing':
          return Availity.PlaceOfService.NURSING_FACILITY
        case 'custodial':
          return Availity.PlaceOfService.CUSTODIAL_CARE_FACILITY
        case 'hospice':
          return Availity.PlaceOfService.HOSPICE
        case 'ambulance_land':
          return Availity.PlaceOfService.AMBULANCE_LAND
        case 'ambulance_air_or_water':
          return Availity.PlaceOfService.AMBULANCE_AIR_OR_WATER
        case 'independent_clinic':
          return Availity.PlaceOfService.INDEPENDENT_CLINIC
        case 'federal_qualified':
          return Availity.PlaceOfService.FEDERALLY_QUALIFIED_HEALTH_CENTER
        case 'inpatient_psych':
          return Availity.PlaceOfService.INPATIENT_PSYCHIATRIC_FACILITY
        case 'psych_partial_hospital':
          return Availity.PlaceOfService
            .PSYCHIATRIC_FACILITY_PARTIAL_HOSPITALIZATION
        case 'mental_health_center':
          return Availity.PlaceOfService.COMMUNITY_MENTAL_HEALTH_CENTER
        case 'mentally_retarded':
          return Availity.PlaceOfService
            .INTERMEDIATE_CARE_FACILITY_MENTALLY_RETARDED
        case 'residential_substance_abuse':
          return Availity.PlaceOfService
            .RESIDENTIAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY
        case 'psych_residential':
          return Availity.PlaceOfService
            .PSYCHIATRIC_RESIDENTIAL_TREATMENT_CENTER
        case 'ambulatory_substance_abuse':
          return Availity.PlaceOfService
            .NON_RESIDENTIAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY
        case 'immunization':
          return Availity.PlaceOfService.MASS_IMMUNIZATION_CENTER
        case 'inpatient_rehab':
          return Availity.PlaceOfService
            .COMPREHENSIVE_INPATIENT_REHABILITATION_FACILITY
        case 'outpatient_rehab':
          return Availity.PlaceOfService
            .COMPREHENSIVE_OUTPATIENT_REHABILITATION_FACILITY
        case 'end_stage_renal':
          return Availity.PlaceOfService
            .END_STATE_RENAL_DISEASE_TREATMENT_FACILITY
        case 'public_clinic':
          return Availity.PlaceOfService.PUBLIC_HEALTH_CLINIC
        case 'rural_clinic':
          return Availity.PlaceOfService.RURAL_HEALTH_CLINIC
        case 'independent_lab':
          return Availity.PlaceOfService.INDEPENDENT_LABORATORY
        case 'other':
          return Availity.PlaceOfService.OTHER_PLACE_OF_SERVICE
      }
    }
  }
}

export interface ElementFormData {
  patient: Patient
  appointment: AppointmentModel
  patientInsurance: PatientInsurance
  carrier: InsuranceProviderLookup
  authorization: AuthorizationModel
}
